/**
 * Application configuration module.
 * Reads configuration from runtime config (window.__ADC_CONFIG__) injected by
 * docker-entrypoint.sh at container startup, with build-time fallbacks for local dev.
 *
 * @module config
 */

/**
 * Runtime configuration injected by docker-entrypoint.sh into /config.js.
 * Falls back to empty object when running locally without the entrypoint.
 */
const runtimeConfig: Record<string, string> =
  ((window as unknown as Record<string, unknown>).__ADC_CONFIG__ as Record<string, string>) ?? {};

interface AppConfig {
  /** Base URL for the ADC Management API */
  apiBaseUrl: string;
  /** Application display name */
  appName: string;
  /** Application version */
  appVersion: string;
  /** Whether the app is running in development mode */
  isDevelopment: boolean;
  /** Whether the app is running in production mode */
  isProduction: boolean;
  /** Whether the app is in preview mode (accessed via preview domain) */
  isPreviewMode: boolean;
  /** URL to the stable portal (only set when in preview mode) */
  stablePortalUrl: string | null;
}

/**
 * Determines if the app is running in preview mode based on the current hostname.
 * Preview mode is enabled when accessed via:
 * - Localhost: always enabled for local development
 * - Dev/Test: hostname containing 'portal-preview' (e.g., portal-preview.user.azuredevcompute-test.io)
 * - Prod: hostname on agentdevcompute.io domain with portal-preview prefix
 *
 * Returns both whether preview mode is enabled and the stable portal URL if applicable.
 */
function detectPreviewMode(): { isPreview: boolean; stableUrl: string | null } {
  const hostname = window.location.hostname;
  // Always enable preview mode on localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { isPreview: true, stableUrl: null };
  }
  // Check for dev/test preview domain pattern (portal-preview.user.azuredevcompute-test.io)
  if (hostname.includes('portal-preview') && hostname.endsWith('azuredevcompute-test.io')) {
    // Replace portal-preview with portal for stable URL
    const stableHostname = hostname.replace('portal-preview', 'portal');
    return { isPreview: true, stableUrl: `https://${stableHostname}` };
  }
  // Check for prod/stage preview domain (portal-preview.*.agentdevcompute.io)
  if (hostname.includes('portal-preview') && hostname.endsWith('agentdevcompute.io')) {
    // Replace portal-preview with portal for stable URL (same domain)
    const stableHostname = hostname.replace('portal-preview', 'portal');
    return { isPreview: true, stableUrl: `https://${stableHostname}` };
  }
  return { isPreview: false, stableUrl: null };
}

/**
 * Derives the management API base URL from the current portal hostname.
 * Maps portal.* to management.* on the same domain family:
 * - portal.agentdevcompute.io → management.agentdevcompute.io (prod main)
 * - portal-preview.agentdevcompute.io → management.agentdevcompute.io (prod preview)
 * - portal.stage.agentdevcompute.io → management.stage.agentdevcompute.io (stage)
 * - portal.user.azuredevcompute-test.io → management-agent.user.azuredevcompute-test.io (dev)
 * Falls back to VITE_API_BASE_URL if set, or localhost for dev.
 */
function resolveApiBaseUrl(): string {
  // Explicit override takes precedence (runtime config or build-time env)
  const configuredUrl = runtimeConfig.API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
  if (configuredUrl) {
    return configuredUrl;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }

  // Dev/test: portal.{stamp}.azuredevcompute-test.io → management-agent.{stamp}.azuredevcompute-test.io
  if (hostname.endsWith('.azuredevcompute-test.io')) {
    const managementHostname = hostname
      .replace(/^portal-preview\./, 'management-agent.')
      .replace(/^portal\./, 'management-agent.');
    return `https://${managementHostname}`;
  }

  // Prod/stage: portal.agentdevcompute.io → management.agentdevcompute.io
  if (hostname.startsWith('portal.') || hostname.startsWith('portal-preview.')) {
    const managementHostname = hostname
      .replace(/^portal-preview\./, 'management.')
      .replace(/^portal\./, 'management.');
    return `https://${managementHostname}`;
  }

  return 'http://localhost:5000';
}

/**
 * Retrieves the application configuration from environment variables.
 * Uses Vite's import.meta.env for build-time environment variable injection.
 */
function getConfig(): AppConfig {
  const apiBaseUrl = resolveApiBaseUrl();
  const { isPreview: isPreviewMode, stableUrl: stablePortalUrl } = detectPreviewMode();

  // Log configuration during initialization
  console.log('[Config] Initializing application configuration');
  console.log('[Config] Runtime config:', runtimeConfig);
  console.log('[Config] Resolved apiBaseUrl:', apiBaseUrl);
  console.log('[Config] Preview Mode:', isPreviewMode);
  console.log('[Config] Stable Portal URL:', stablePortalUrl);

  return {
    apiBaseUrl,
    appName: 'ADC Management Console',
    appVersion: '1.0.0',
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    isPreviewMode,
    stablePortalUrl,
  };
}

/**
 * Singleton configuration object.
 * Access this throughout the application for consistent configuration.
 */
export const config = getConfig();

/**
 * Validate that required configuration is present.
 * Call this early in the application lifecycle.
 */
export function validateConfig(): void {
  console.log('[Config] Validating configuration...');
  const requiredFields: (keyof AppConfig)[] = ['apiBaseUrl'];

  for (const field of requiredFields) {
    if (!config[field]) {
      console.warn(`[Config] Configuration warning: ${field} is not set`);
    } else {
      console.log(`[Config] ${field}: ${config[field]}`);
    }
  }
  console.log('[Config] Validation complete');
}
